import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Countdown, StatusBadge, Thumb, formatDate, imgUrl, money, reveal } from "./shared";

const Info = ({ k, v }) => (
  <div className="la-field">
    <div className="k">{k}</div>
    <div className="v">{v === null || v === undefined || v === "" ? "-" : v}</div>
  </div>
);

const Person = ({ role, name, email, pic, userId }) => (
  <div className="la-seller w-100">
    <div className="la-avatar">
      {pic ? <img alt="" src={imgUrl(pic)} /> : <i className="bi bi-person"></i>}
    </div>
    <div>
      <div className="la-sub">{role}</div>
      <div className="la-title">{name || email || "-"}</div>
      <div className="la-sub">
        {name && email ? `${email}` : ""}
        {userId ? `${name && email ? " · " : ""}User #${userId}` : ""}
      </div>
    </div>
  </div>
);

const BidDetailView = ({ detail }) => {
  const navigate = useNavigate();
  const images = Array.isArray(detail?.images) ? detail.images : [];
  const [active, setActive] = useState(0);

  const num = (k) => Number(detail?.[k]) || 0;
  const total = num("amount") + num("shipping_fee") + num("import_duty") + num("custom_duty");
  const isWinner = detail?.winner_user_id && detail.winner_user_id === detail.user_id;
  const isLeading = detail?.status === "active" && Number(detail?.amount) >= Number(detail?.current_price);

  const breakdown = [
    ["Bid Amount", money(detail?.amount)],
    ["Shipping Fee", money(detail?.shipping_fee)],
    ["Import Duty", money(detail?.import_duty)],
    ["Custom Duty", money(detail?.custom_duty)],
  ];

  const auctionStats = [
    ["Current Price", money(detail?.current_price), "highlight"],
    ["Starting Price", money(detail?.starting_price), ""],
    ["Bid Increment", money(detail?.bid_increment), ""],
    ["Reserve Price", money(detail?.reserve_price), ""],
    ["Buy Now", money(detail?.buy_now_price), ""],
    ["Total Bids", detail?.bid_count ?? 0, ""],
  ];

  return (
    <>
      <div className="la-card la-hero la-hero-top la-reveal">
        <div className="la-gallery">
          {images.length ? (
            <div className="la-main-wrap"><img key={active} className="la-main-img" alt="" src={imgUrl(images[active])} /></div>
          ) : (
            <div className="la-main-wrap"><i className="bi bi-image"></i></div>
          )}
          {images.length > 1 && (
            <div className="la-thumbs">
              {images.map((im, i) => (
                <button key={im} className={`la-thumb-btn ${i === active ? "active" : ""}`} onClick={() => setActive(i)}>
                  <Thumb src={im} />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-grow-1">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-1">
            <div className="la-sub">Bid #{detail?.id} · Auction #{detail?.live_auction_id}</div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate(`/seller-auctions/${detail?.live_auction_id}`)}
            >
              <i className="bi bi-box-arrow-up-right me-1"></i>View Auction
            </button>
          </div>
          <h3 className="mb-2">{detail?.product_name || "-"}</h3>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <StatusBadge status={detail?.status} />
            {isWinner && <span className="la-chip win"><i className="bi bi-trophy-fill me-1"></i>Winner</span>}
            {isLeading && <span className="la-chip lead"><i className="bi bi-arrow-up-circle-fill me-1"></i>Highest bid</span>}
            <span className="la-chip">Auction: {detail?.auction_status || "-"}</span>
            {detail?.condition && <span className="la-chip">{detail.condition}</span>}
            {detail?.size && <span className="la-chip">{detail.size}</span>}
            {detail?.category_name && <span className="la-chip">{detail.category_name}</span>}
          </div>

          <div className="la-bid-amount" style={{ animation: "la-pop .6s .2s both" }}>
            <div className="la-sub">Bid Amount</div>
            <div className="la-bid-value">{money(detail?.amount)}</div>
            <div className="la-sub">Placed {formatDate(detail?.created_at)}</div>
          </div>

          <div className="la-people">
            <Person
              role="Bidder"
              name={detail?.bidder_name}
              email={detail?.bidder_email}
              pic={detail?.bidder_pic}
              userId={detail?.user_id}
            />
            <Person role="Seller" name={detail?.seller_name} email={detail?.seller_email} />
          </div>
        </div>
      </div>

      {String(detail?.auction_status).toLowerCase() === "live" && detail?.end_at && (
        <div className="la-timer la-reveal" style={reveal(1)}>
          <div>
            <div className="k">Auction ends in</div>
            <div className="v"><Countdown end={detail.end_at} /></div>
          </div>
        </div>
      )}

      <div className="la-two-col la-reveal" style={reveal(2)}>
        <div className="la-card">
          <div className="la-section-title"><i className="bi bi-receipt"></i>Cost Breakdown</div>
          <div className="la-breakdown">
            {breakdown.map(([k, v]) => (
              <div key={k} className="la-row">
                <span>{k}</span>
                <span className="la-amount">{v}</span>
              </div>
            ))}
            <div className="la-row la-total">
              <span>Total</span>
              <span>{money(total)}</span>
            </div>
          </div>
        </div>
        <div className="la-card">
          <div className="la-section-title"><i className="bi bi-credit-card"></i>Payment & Schedule</div>
          <div className="la-fields">
            <Info k="Payment Method" v={detail?.payment_method_id} />
            <Info k="Bid Placed" v={formatDate(detail?.created_at)} />
            <Info k="Auction Starts" v={formatDate(detail?.start_at)} />
            <Info k="Auction Ends" v={formatDate(detail?.end_at)} />
          </div>
        </div>
      </div>

      <div className="la-section-label la-reveal" style={reveal(3)}>Auction Snapshot</div>
      <div className="la-stats">
        {auctionStats.map(([k, v, cls], n) => (
          <div key={k} className={`la-card la-stat la-reveal ${cls}`} style={reveal(n + 3)}>
            <div className="k">{k}</div>
            <div className="v">{v}</div>
          </div>
        ))}
      </div>

      <div className="la-card mb-3 la-reveal" style={reveal(9)}>
        <div className="la-section-title"><i className="bi bi-trophy"></i>Result</div>
        <div className="la-fields">
          <Info k="Winner" v={detail?.winner_user_id ? `User #${detail.winner_user_id}` : null} />
          <Info k="Winning Amount" v={detail?.winning_amount != null ? money(detail.winning_amount) : null} />
          <Info k="Sold Via" v={detail?.sold_via} />
          <Info k="Closed At" v={detail?.closed_at ? formatDate(detail.closed_at) : null} />
          <Info
            k="Order"
            v={
              detail?.order_id ? (
                <button className="btn btn-link p-0" onClick={() => navigate(`/view-orders/${detail.order_id}`)}>
                  #{detail.order_id}
                </button>
              ) : null
            }
          />
        </div>
      </div>
    </>
  );
};

export default BidDetailView;
